import { CLIMB_RATES } from '../engine/battlefield/climb';
import styles from '../styles/Battlefield.module.css';

/**
 * What climbing costs, written out.
 *
 * IT LIVES IN THE LEGEND for the same reason the cover formula does:
 * both modes have it and both roles can open it. Until now the only
 * prose about climbing anywhere in the app was in the GM permissions
 * dialog - so a player could watch a climb eat their movement with no
 * way to find out why, at what rate, or that their own training was
 * what made it cheaper. A rule nobody can find is a rule nobody knows.
 *
 * The rates come from `CLIMB_RATES` rather than being retyped, so this
 * page cannot drift from what the map actually charges.
 */
export default function ClimbFormula({ maxStep, rate, costs = true, trainingCounts = true }) {
  const m = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const stride = Number.isFinite(maxStep) ? `${m(maxStep)} m` : 'a stride';

  return (
    <div className={styles.coverFormulaBody} data-testid="climb-formula">
      <p>
        A wall is <strong>high, not impassable</strong>. What stops a climb is your <strong>movement</strong>, not the
        wall - five metres of rise at the trained rate is ten metres of movement, and thirty metres is a day&apos;s
        work.
      </p>
      <p>
        <code>metres of rise × your rate</code>, charged against the move:
      </p>
      <ul>
        <li>
          <strong>{m(CLIMB_RATES.untrained)} m</strong> per metre - untrained
        </li>
        <li>
          <strong>{m(CLIMB_RATES.trained)} m</strong> - Trained
        </li>
        <li>
          <strong>{m(CLIMB_RATES.plus10)} m</strong> - Climb +10
        </li>
        <li>
          <strong>{m(CLIMB_RATES.plus20)} m</strong> - Climb +20, as fast as they walk
        </li>
      </ul>
      <p>
        <strong>No test is rolled.</strong> Training sets the rate, so a move is never interrupted to ask for a die.
      </p>
      <p>
        <strong>Coming down costs the same.</strong> A drop within <strong>{stride}</strong> - one stride, a third of
        your own height - is free; beyond that it is a climb down at the same rate. So a Dreadnought hops off what a
        guardsman has to climb. Falling is not modelled: a long drop is the table&apos;s to rule on.
      </p>
      {rate !== undefined && (
        <p>
          This one is being charged <strong>{m(rate)} m per metre</strong>
          {!trainingCounts && ' - the Climb skill is not being counted'}.
        </p>
      )}
      {!costs && (
        <p>
          <strong>Climbing is switched off.</strong> A rise beyond {stride} is refused outright, as it was before the
          rule existed - and a drop is <strong>not</strong>, because with no way up a refused way down would make every
          roof a prison.
        </p>
      )}
    </div>
  );
}
